// Full-screen login in the Epicure voice: big headline with the italic-green
// flourish, one card, one pill button. Name + the authenticator's 6-digit
// code; there is no password field (decision 2026-10-31 — accounts log in
// with the app only). Sessions last 30 days (sliding), so the tablet sees
// this roughly never.

import { useState, type FormEvent } from "react";

import { ApiError, post } from "@/api/client";
import { cn } from "@/lib/utils";

export function Login({ onLogin }: { onLogin: () => void }) {
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await post("/api/auth/login", { username, password: code });
      onLogin();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? "Too many attempts — wait a moment."
          : "Wrong name or code, chef.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-10 bg-background p-6">
      <h1 className="text-center font-display text-7xl leading-[0.95] font-medium tracking-[-0.02em] text-foreground">
        <span className="block pb-1 italic text-brand-ink">yes,</span>
        Chef
      </h1>

      <form
        onSubmit={submit}
        className="flex w-full max-w-sm flex-col gap-3 rounded-[var(--radius-card)] border border-line-strong bg-surface p-6 shadow-[0_15px_40px_rgba(0,0,0,0.2)]"
      >
        <label className="flex flex-col gap-1.5 text-xs text-dim">
          Name
          <input
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="rounded-lg border border-line-strong bg-transparent px-3 py-2.5 text-sm text-foreground outline-none focus:border-brand-ink"
          />
        </label>
        <label className="flex flex-col gap-1.5 text-xs text-dim">
          Authenticator code
          <input
            type="password"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="rounded-lg border border-line-strong bg-transparent px-3 py-2.5 font-mono text-sm tracking-[0.2em] text-foreground outline-none focus:border-brand-ink"
          />
        </label>

        {error && <p className="text-xs text-status-error">{error}</p>}

        <button
          type="submit"
          disabled={busy || !username || !code}
          className={cn(
            "mt-2 cursor-pointer rounded-full bg-brand px-5 py-3 text-sm font-bold text-primary-foreground",
            "transition-[transform,background-color,opacity] duration-500 ease-epicure",
            "hover:-translate-y-0.5 hover:bg-[var(--brand-hover)] active:scale-[0.98]",
            "disabled:cursor-default disabled:opacity-35 disabled:hover:translate-y-0",
          )}
        >
          {busy ? "…" : "Enter the kitchen"}
        </button>
      </form>
    </div>
  );
}
